import Button from "atoms/buttons/button";
import Loader from "atoms/loaders/loader";
import Paragraph from "atoms/typography/paragraph";
import AlertLevels from "constants/alert-levels";
import GroupMigrationRequestDto from "models/interfaces/group-migration-request";
import GroupMemberRecord from "models/view-models/group-member-record";
import GroupMigrationRequestRecord from "models/view-models/group-migration-request-record";
import SupportUserSubscriptionRecord from "models/view-models/support-user-subscription-record";
import AlertBanner from "molecules/alerts/alert-banner";
import UnorderedList from "molecules/lists/unordered-list";
import { ConfirmationModal } from "molecules/modals/confirmation-modal";
import BaseModalFooter from "molecules/modals/footers/base-modal-footer";
import useSupportManageTeamMembers from "organisms/modals/support-user-team-manage-modal/use-support-manage-team-members";
import useSupportUserTeamManagement from "organisms/modals/support-user-team-manage-modal/use-support-user-team-management";
import ManageTeamInviteField from "pages/team-onboarding/manage-team-settings/manage-team-invite-field";
import ManageTeamMembersList from "pages/team-onboarding/manage-team-settings/manage-team-members-list";
import React, { useEffect, useState } from "react";
import useSupportUserDetail from "utilities/hooks/domain/admin/support/use-support-user-detail";
import useModalActions from "utilities/hooks/use-modal-actions";
import { t } from "utilities/localization-utils";

const CSS_CLASS_NAME = "c-support-user-team-manage-modal";

interface ModalPhaseUpdateTeamMembersProps {
    subscription: SupportUserSubscriptionRecord;
    selectedTeamId: number;
    onMigrateConfirm: (
        groupMigrationDto: GroupMigrationRequestDto
    ) => Promise<GroupMigrationRequestRecord | undefined>;
    onBack: () => void;
    onSuccess: (migrationRequest: GroupMigrationRequestRecord) => void;
}
const ModalPhaseUpdateTeamMembers = ({
    subscription,
    selectedTeamId,
    onMigrateConfirm,
    onBack,
    onSuccess,
}: ModalPhaseUpdateTeamMembersProps) => {
    const { isOpen, handleToggle } = useModalActions();
    const { ownerId } = useSupportUserTeamManagement();
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState("");
    const { supportUser: owner, getUserDetail } = useSupportUserDetail();

    const {
        emailError,
        handleAddClick,
        handleOnChange,
        inviteEmail,
        teamMembersToMigrate,
        emailsToInvite,
        setTeamMembersToMigrate,
        setEmailsToInvite,
        isLoading: isLoadingTeamMembers,
        teamMigrationSummary,
    } = useSupportManageTeamMembers(
        selectedTeamId,
        subscription,
        owner?.legacyId
    );

    useEffect(() => {
        getUserDetail(ownerId);
    }, [getUserDetail, ownerId]);

    const teamOwner = new GroupMemberRecord().with({
        email: owner?.email,
        lastName: owner?.fullName,
        legacyId: owner?.legacyId,
    });

    const teamMembersWithoutOwner = teamMembersToMigrate.filter(
        (m) => m.email !== teamOwner.email
    );

    const handleConfirmMigrate = async () => {
        setIsLoading(true);
        try {
            const migrationResult = await onMigrateConfirm({
                targetGroupId: subscription.groupId,
                sourceGroupId: selectedTeamId,
                teamMemberEmailsToInvite: emailsToInvite,
                teamMembersToMove: teamMembersToMigrate,
            });

            if (migrationResult == null) {
                setErrors("Migration returned no result.");
                return;
            }

            onSuccess(migrationResult);
        } catch (error) {
            setErrors(JSON.stringify(error));
        } finally {
            setIsLoading(false);
        }
    };

    const label = errors ? "Errors" : "Confirm Team Migration";
    const message = errors
        ? "The migration failed with the following errors:"
        : "Are you sure you want to migrate the team?";

    return (
        <>
            <div className={`${CSS_CLASS_NAME}__update-team-members`}>
                <div className={`${CSS_CLASS_NAME}__content`}>
                    <AlertBanner alertLevel={AlertLevels.Info}>
                        Remove previous members of the source team and invite
                        any new members. To invite members in bulk, add a list
                        of comma-separated emails.
                    </AlertBanner>
                    <br />
                    {isLoadingTeamMembers ? (
                        <Loader />
                    ) : (
                        <>
                            <ManageTeamInviteField
                                emailError={emailError}
                                handleOnChange={handleOnChange}
                                inviteEmail={inviteEmail}
                                handleAddClick={handleAddClick}
                                totalSeatsRemaining={
                                    teamMigrationSummary.seatsRemaining
                                }
                                totalExistingMembers={
                                    teamMigrationSummary.existingMembers
                                }
                            />
                            <ManageTeamMembersList
                                teamOwner={teamOwner}
                                teamMembersToMigrate={teamMembersWithoutOwner}
                                emailsToInvite={emailsToInvite}
                                onUpdateTeamMembersToMigrate={
                                    setTeamMembersToMigrate
                                }
                                onUpdateEmailsToInvite={setEmailsToInvite}
                                areGroupMembersLoading={false}
                                totalSeatsCurrentlyTaken={
                                    teamMigrationSummary.membersToAdd
                                }
                                totalSeatsRemaining={
                                    teamMigrationSummary.seatsRemaining
                                }
                            />
                        </>
                    )}
                </div>
                <BaseModalFooter
                    primaryText={t("next")}
                    primaryButtonProps={{
                        onClick: handleToggle,
                        disabled: teamMigrationSummary.seatsRemaining < 0,
                    }}
                    closeButtonProps={{ onClick: onBack }}
                    closeButtonText={t("back")}
                    closeDialog={onBack}
                />
            </div>
            <ConfirmationModal
                isVisible={isOpen}
                message={message}
                label={label}
                onCancel={handleToggle}
                onConfirm={handleConfirmMigrate}>
                {isLoading && <Loader />}

                {errors && (
                    <AlertBanner alertLevel={AlertLevels.Error}>
                        {errors}
                        <div>
                            <Button onClick={() => setErrors("")}>
                                Try again
                            </Button>
                        </div>
                    </AlertBanner>
                )}

                {!isLoading && !errors && (
                    <>
                        <Paragraph>
                            <strong>Migrating members</strong>:
                            {teamMembersToMigrate.length ? (
                                <UnorderedList
                                    listItems={teamMembersToMigrate.map(
                                        (member) =>
                                            `${member.getFirstAndLastName()} (${member.email})`
                                    )}
                                />
                            ) : (
                                <Paragraph>
                                    <em>None</em>
                                </Paragraph>
                            )}
                        </Paragraph>
                        <Paragraph>
                            <strong>New invites</strong>:
                            {emailsToInvite.length ? (
                                <UnorderedList listItems={emailsToInvite} />
                            ) : (
                                <Paragraph>
                                    <em>None</em>
                                </Paragraph>
                            )}
                        </Paragraph>
                    </>
                )}
            </ConfirmationModal>
        </>
    );
};

export default ModalPhaseUpdateTeamMembers;
